/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { QueryCache, QueryClient } from '@tanstack/react-query';

// Created on first use in the browser / React Native, then reused for the
// lifetime of the JS context.
let browserQueryClient: QueryClient | undefined;

/**
 * A QueryClient for a single server render.
 *
 * Every query it builds gets `gcTime: Infinity`, TanStack's own server default,
 * whatever the hook or prefetch asked for. A finite gcTime (many hooks set 30-60
 * minutes) registers a real `setTimeout` per query, and that timer keeps
 * Query -> QueryCache -> every query in this client -> the render's context
 * reachable until it fires, so each request stayed resident for up to an hour.
 * Infinity registers no timer. It is forced at build time because per-query
 * options beat `defaultOptions`, and react-query keeps the larger gcTime on
 * later option updates, so it stays Infinity.
 */
export function createServerQueryClient(): QueryClient {
  const queryCache = new QueryCache();
  const build = queryCache.build.bind(queryCache);
  queryCache.build = ((client, options, state) =>
    build(
      client,
      { ...options, gcTime: Infinity },
      state,
    )) as QueryCache['build'];
  return new QueryClient({ queryCache });
}

/**
 * Returns the QueryClient to hand to `QueryClientProvider`.
 *
 * - Server (SSR): a fresh `createServerQueryClient()` every call. A module
 *   singleton on the server is shared by every concurrent request in the Node
 *   process, which would leak one user's cached data into another user's response.
 * - Browser / React Native: a stable singleton, so the cache survives re-renders
 *   and remounts of the provider instead of being thrown away with it.
 *
 * `typeof window` is the server check rather than a platform check on purpose:
 * React Native defines `window`, so native correctly takes the singleton path.
 */
export function getQueryClient(): QueryClient {
  if (typeof window === 'undefined') {
    return createServerQueryClient();
  }

  if (!browserQueryClient) {
    browserQueryClient = new QueryClient();
  }

  return browserQueryClient;
}
